import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { NAV } from "@/constants/testIds";
import { useSidebar } from "./SidebarContext";
import { getUser } from "@/lib/api";
import {
  LayoutDashboard,
  RefreshCw,
  Package,
  Boxes,
  Cable,
  Building2,
  DatabaseBackup,
  ChevronsLeft,
  ChevronsRight,
  Factory,
  Beaker,
  Activity,
  Truck,
  HardHat,
  Receipt,
  Warehouse,
  Handshake,
  Wallet,
  Sliders,
  BookOpen,
  ClipboardList,
  Percent,
  ShieldAlert,
  Building,
  Users,
  Tags,
  FileText,
  PackageOpen,
  UserCog,
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_4a2dee35-189a-4f59-95bd-a089560b8efb/artifacts/rzc72v1x_thara%20%281%29.png";

// Grouped nav — each group has a label + array of items.
// `mod` = permission key (see /api/users/module-catalog). Superadmin bypasses.
const GROUPS = [
  {
    label: "Retail Stocks Tracking",
    items: [
      { to: "/", icon: LayoutDashboard, label: "Dashboard", testid: NAV.dashboardLink, end: true, mod: "dashboard" },
      { to: "/sync", icon: RefreshCw, label: "Sync Monitor", testid: NAV.syncMonitorLink, mod: "sync_monitor" },
      { to: "/stock", icon: Boxes, label: "Stock Monitoring", testid: NAV.stockMonitoringLink, mod: "stock_monitoring" },
      { to: "/products", icon: Package, label: "Products", testid: NAV.productsLink, mod: "products" },
      { to: "/branches", icon: Building2, label: "Branches", testid: "nav-branches", mod: "branches" },
    ],
  },
  {
    label: "Production Operations",
    items: [
      { to: "/production/monitor", icon: Activity, label: "Production Monitor", testid: "nav-production-monitor", mod: "production_monitor" },
      { to: "/production/units", icon: Factory, label: "Production Units", testid: "nav-production-units", mod: "production_units" },
      { to: "/production/warehouses", icon: Building2, label: "Warehouses", testid: "nav-warehouses", mod: "warehouses" },
      { to: "/production/raw-materials", icon: Beaker, label: "Raw Materials", testid: "nav-raw-materials", mod: "raw_materials" },
      { to: "/production/rm-vendors", icon: Truck, label: "RM Vendors", testid: "nav-rm-vendors", mod: "rm_vendors" },
      { to: "/production/workers", icon: HardHat, label: "Workers", testid: "nav-workers", mod: "workers" },
      { to: "/production/rm-purchase-orders", icon: ClipboardList, label: "Purchase Orders", testid: "nav-rm-purchase-orders", mod: "purchase_orders" },
      { to: "/production/rm-purchases", icon: Receipt, label: "RM Purchases (GRN)", testid: "nav-rm-purchases", mod: "rm_purchases" },
      { to: "/production/rm-stocks", icon: Warehouse, label: "RM Stocks", testid: "nav-rm-stocks", mod: "rm_stocks" },
      { to: "/production/fabrications", icon: Factory, label: "L1 Fabrication", testid: "nav-fabrications", mod: "l1_fabrication" },
      { to: "/production/platings", icon: Handshake, label: "L2 Plating", testid: "nav-platings", mod: "l2_plating" },
      { to: "/production/quality-control", icon: HardHat, label: "Quality Control", testid: "nav-quality-control", mod: "quality_control" },
      { to: "/production/warehouse-stocks", icon: Warehouse, label: "Warehouse Stock", testid: "nav-warehouse-stocks", mod: "warehouse_stock" },
      { to: "/production/worker-ledger", icon: Wallet, label: "Worker Ledger", testid: "nav-worker-ledger", mod: "worker_ledger" },
      { to: "/production/vendor-ledger", icon: BookOpen, label: "Vendor Ledger", testid: "nav-vendor-ledger", mod: "vendor_ledger" },
      { to: "/production/taxes", icon: Percent, label: "Tax & Other Settings", testid: "nav-taxes", mod: "tax_settings" },
    ],
  },
  {
    label: "Sales & Distribution",
    items: [
      { to: "/sales/branches", icon: Building, label: "Branches / Franchises", testid: "nav-sales-branches", mod: "branches" },
      { to: "/sales/customers", icon: Users, label: "Customers", testid: "nav-customers", mod: "sales_customers" },
      { to: "/sales/product-prices", icon: Tags, label: "Product Prices", testid: "nav-product-prices", mod: "product_prices" },
      { to: "/sales/invoices", icon: FileText, label: "Sales Invoices", testid: "nav-sales-invoices", mod: "sales_invoices" },
      { to: "/sales/customer-ledger", icon: BookOpen, label: "Customer Ledger", testid: "nav-customer-ledger", mod: "customer_ledger" },
      { to: "/sales/franchise-ledger", icon: BookOpen, label: "Franchise Ledger", testid: "nav-franchise-ledger", mod: "franchise_ledger" },
      { to: "/sales/stock-transfers", icon: Truck, label: "Stock Transfers", testid: "nav-stock-transfers", mod: "stock_transfers" },
      { to: "/sales/stock-movements", icon: Activity, label: "Warehouse Stock Movements", testid: "nav-stock-movements", mod: "stock_movements" },
      { to: "/sales/branch-stock", icon: PackageOpen, label: "Branch & Franchise Stock", testid: "nav-branch-stock", mod: "branch_stock" },
      { to: "/sales/stock-requests", icon: Truck, label: "Store Stock Requests", testid: "nav-stock-requests", mod: "stock_requests" },
    ],
  },
  {
    label: "Integration & Setup",
    items: [
      { to: "/admin/users", icon: UserCog, label: "Users & Privileges", testid: "nav-users", mod: "users" },
      { to: "/settings", icon: Sliders, label: "Settings", testid: "nav-settings", mod: "business_settings" },
      { to: "/backups", icon: DatabaseBackup, label: "Backups", testid: "nav-backups", mod: "backups", adminOnly: true },
      { to: "/wings-api-spec", icon: Cable, label: "Wings API Spec", testid: NAV.apiDocsLink, mod: "api_docs" },
    ],
  },
];

const isAdmin = () => {
  const u = getUser();
  return u && ((u.role || "").toLowerCase().includes("admin") || (u.role || "").toLowerCase() === "superadmin");
};

// Check if the current user is allowed to see `moduleKey`. Superadmin sees all.
const canAccess = (moduleKey) => {
  const u = getUser();
  if (!u) return false;
  const role = (u.role || "").toLowerCase();
  if (role === "superadmin" || role === "administrator" || role === "admin") return true;
  if (!moduleKey) return true;
  return (u.allowed_modules || []).includes(moduleKey);
};

export default function Sidebar() {
  const location = useLocation();
  const { collapsed, hidden, toggle } = useSidebar();

  if (hidden) return null;

  return (
    <aside
      className={cn(
        "fixed inset-y-0 left-0 bg-white border-r border-gray-200 flex flex-col z-30 transition-[width] duration-200",
        collapsed ? "w-16" : "w-64"
      )}
    >
      <div className={cn(
        "h-16 flex items-center gap-3 border-b border-gray-200 relative",
        collapsed ? "px-2 justify-center" : "px-5"
      )}>
        <img src={LOGO_URL} alt="Thara" className="h-9 w-auto object-contain shrink-0" />
        {!collapsed && (
          <div className="leading-tight">
            <p className="font-heading text-sm font-bold text-gray-900">Thara Gold</p>
            <p className="text-[10px] uppercase tracking-widest text-gray-500">Inventory Suite</p>
          </div>
        )}
        <button
          onClick={toggle}
          data-testid="sidebar-toggle"
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          className="absolute -right-3 top-1/2 -translate-y-1/2 h-6 w-6 rounded-full bg-white border border-gray-200 shadow-sm flex items-center justify-center hover:border-[#9B1C1C] hover:text-[#9B1C1C] transition-colors z-10"
        >
          {collapsed ? <ChevronsRight className="h-3 w-3" /> : <ChevronsLeft className="h-3 w-3" />}
        </button>
      </div>

      <nav className="flex-1 py-3 overflow-y-auto overflow-x-hidden">
        {GROUPS.map((group, gi) => {
          const visibleItems = group.items.filter((it) => {
            if (it.adminOnly && !isAdmin()) return false;
            return canAccess(it.mod);
          });
          if (visibleItems.length === 0) return null;
          return (
            <div key={group.label} className={gi === 0 ? "" : "mt-3 pt-3 border-t border-gray-100"}>
              {!collapsed && (
                <p className="px-6 pb-1.5 text-[10px] font-semibold uppercase tracking-widest text-gray-400">
                  {group.label}
                </p>
              )}
              {visibleItems.map((item) => {
                const Icon = item.icon;
                const active = item.end
                  ? location.pathname === item.to
                  : location.pathname.startsWith(item.to);
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    data-testid={item.testid}
                    end={item.end}
                    title={collapsed ? item.label : undefined}
                    className={cn(
                      "flex items-center gap-3 rounded-md text-sm font-medium transition-colors",
                      collapsed ? "justify-center mx-2 my-0.5 h-10 w-12" : "px-3 py-2 mx-3 my-0.5",
                      active
                        ? "bg-red-50 text-[#9B1C1C]"
                        : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                    )}
                  >
                    <Icon className="h-4 w-4 shrink-0" strokeWidth={1.75} />
                    {!collapsed && <span>{item.label}</span>}
                    {active && !collapsed && <span className="ml-auto h-1.5 w-1.5 rounded-full bg-[#9B1C1C]" />}
                  </NavLink>
                );
              })}
            </div>
          );
        })}
      </nav>

      {!collapsed && (
        <div className="border-t border-gray-200 p-4">
          <div className="rounded-md bg-gradient-to-br from-amber-50 to-red-50 border border-amber-200 p-3">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-amber-700">
              Wings ERP
            </p>
            <p className="text-xs text-gray-700 mt-1 leading-snug">
              Real-time data sync running every 15 minutes
            </p>
            <div className="mt-2 flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-[11px] text-emerald-700 font-medium">Connected</span>
            </div>
          </div>
        </div>
      )}
      {collapsed && (
        <div className="border-t border-gray-200 py-3 flex justify-center">
          <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" title="Wings ERP Connected" />
        </div>
      )}
    </aside>
  );
}
